import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import type { BuildPreset, BuildPresetSource, BuildPresetStage, RaidId } from '../../types'
import {
  GuildBuildPresetCreateDocument,
  GuildBuildPresetRemoveDocument,
  GuildBuildPresetUpdateDocument,
  MyBuildPresetCreateDocument,
  MyBuildPresetRemoveDocument,
  MyBuildPresetsDocument,
  MyBuildPresetUpdateDocument,
  RaidPlayerApplyBuildPresetDocument,
} from './graphql'
import { mapBuildPreset } from './mappers'
import type { BuildPresetNode, MyBuildPresetsQuery } from './mappers'

const apolloClient = resolveClient()

type ReportingRunner = <T>(
  mutation: object,
  variables: Record<string, string | number | ReadonlyArray<string> | null>,
  acknowledge?: (result: T) => boolean,
) => Promise<boolean>

interface PresetListMutation {
  readonly myBuildPresetCreate?: ReadonlyArray<BuildPresetNode>
  readonly myBuildPresetUpdate?: ReadonlyArray<BuildPresetNode>
  readonly myBuildPresetRemove?: ReadonlyArray<BuildPresetNode>
}

interface BooleanMutation {
  readonly guildBuildPresetCreate?: boolean
  readonly guildBuildPresetUpdate?: boolean
  readonly guildBuildPresetRemove?: boolean
  readonly raidPlayerApplyBuildPreset?: boolean
}

export interface BuildPresetActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly activeRaidId: Ref<RaidId | null>
  readonly discordUserId: Readonly<Ref<string>>
  readonly myBuildPresets: Ref<ReadonlyArray<BuildPreset>>
  readonly myBuildPresetsLoading: Ref<boolean>
  readonly myBuildPresetsError: Ref<string | null>
  readonly error: Ref<string | null>
  readonly runReporting: ReportingRunner
}

export interface BuildPresetActionsApi {
  readonly fetchMyBuildPresets: () => Promise<void>
  readonly createMyBuildPreset: (name: string, stages: ReadonlyArray<BuildPresetStage>) => Promise<boolean>
  readonly updateMyBuildPreset: (presetId: string, name: string, stages: ReadonlyArray<BuildPresetStage>) => Promise<boolean>
  readonly removeMyBuildPreset: (presetId: string) => Promise<boolean>
  readonly createGuildBuildPreset: (name: string, stages: ReadonlyArray<BuildPresetStage>) => Promise<boolean>
  readonly updateGuildBuildPreset: (presetId: string, name: string, stages: ReadonlyArray<BuildPresetStage>) => Promise<boolean>
  readonly removeGuildBuildPreset: (presetId: string) => Promise<boolean>
  readonly applyBuildPreset: (playerId: string, presetId: string, source: BuildPresetSource) => Promise<boolean>
}

function encodedStages(stages: ReadonlyArray<BuildPresetStage>): ReadonlyArray<string> {
  return stages.map((stage) => JSON.stringify({
    stageKind: stage.stageKind,
    weaponSlugs: [...stage.weaponSlugs],
  }))
}

function mutationPresets(
  data: PresetListMutation | null | undefined,
): ReadonlyArray<BuildPresetNode> | null {
  return data?.myBuildPresetCreate
    ?? data?.myBuildPresetUpdate
    ?? data?.myBuildPresetRemove
    ?? null
}

export function useBuildPresetActions(ctx: BuildPresetActionsCtx): BuildPresetActionsApi {
  function requireIdentity(): string | null {
    const id = ctx.discordUserId.value
    if (id !== '0') return id
    ctx.myBuildPresetsError.value = 'Sign in to manage your build presets.'
    return null
  }

  async function fetchMyBuildPresets(): Promise<void> {
    const discordUserId = requireIdentity()
    if (discordUserId === null) return
    ctx.myBuildPresetsLoading.value = true
    ctx.myBuildPresetsError.value = null
    try {
      const result = await apolloClient.query({
        query: MyBuildPresetsDocument,
        variables: { discordUserId },
        fetchPolicy: 'network-only',
      })
      const data = result.data as MyBuildPresetsQuery | null
      ctx.myBuildPresets.value = data?.myBuildPresets.map(mapBuildPreset) ?? []
    } catch (error: unknown) {
      ctx.myBuildPresetsError.value = error instanceof Error ? error.message : String(error)
    } finally {
      ctx.myBuildPresetsLoading.value = false
    }
  }

  async function mutateMyPresets(
    mutation: object,
    variables: Record<string, string | number | ReadonlyArray<string>>,
  ): Promise<boolean> {
    ctx.myBuildPresetsError.value = null
    try {
      const result = await apolloClient.mutate({ mutation, variables })
      const presets = mutationPresets(result.data as PresetListMutation | null)
      if (presets === null) throw new Error('Build preset mutation returned no snapshot.')
      ctx.myBuildPresets.value = presets.map(mapBuildPreset)
      return true
    } catch (error: unknown) {
      ctx.myBuildPresetsError.value = error instanceof Error ? error.message : String(error)
      return false
    }
  }

  function createMyBuildPreset(
    name: string,
    stages: ReadonlyArray<BuildPresetStage>,
  ): Promise<boolean> {
    const discordUserId = requireIdentity()
    return discordUserId === null
      ? Promise.resolve(false)
      : mutateMyPresets(MyBuildPresetCreateDocument, {
          discordUserId,
          name,
          stages: encodedStages(stages),
        })
  }

  function updateMyBuildPreset(
    presetId: string,
    name: string,
    stages: ReadonlyArray<BuildPresetStage>,
  ): Promise<boolean> {
    const discordUserId = requireIdentity()
    return discordUserId === null
      ? Promise.resolve(false)
      : mutateMyPresets(MyBuildPresetUpdateDocument, {
          discordUserId,
          presetId: Number(presetId),
          name,
          stages: encodedStages(stages),
        })
  }

  function removeMyBuildPreset(presetId: string): Promise<boolean> {
    const discordUserId = requireIdentity()
    return discordUserId === null
      ? Promise.resolve(false)
      : mutateMyPresets(MyBuildPresetRemoveDocument, { discordUserId, presetId: Number(presetId) })
  }

  function createGuildBuildPreset(
    name: string,
    stages: ReadonlyArray<BuildPresetStage>,
  ): Promise<boolean> {
    return ctx.runReporting<BooleanMutation>(GuildBuildPresetCreateDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      name,
      stages: encodedStages(stages),
    }, (result) => result.guildBuildPresetCreate === true)
  }

  function updateGuildBuildPreset(
    presetId: string,
    name: string,
    stages: ReadonlyArray<BuildPresetStage>,
  ): Promise<boolean> {
    return ctx.runReporting<BooleanMutation>(GuildBuildPresetUpdateDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      presetId: Number(presetId),
      name,
      stages: encodedStages(stages),
    }, (result) => result.guildBuildPresetUpdate === true)
  }

  function removeGuildBuildPreset(presetId: string): Promise<boolean> {
    return ctx.runReporting<BooleanMutation>(GuildBuildPresetRemoveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      presetId: Number(presetId),
    }, (result) => result.guildBuildPresetRemove === true)
  }

  function applyBuildPreset(
    playerId: string,
    presetId: string,
    source: BuildPresetSource,
  ): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) {
      ctx.error.value = 'A raid must be selected before applying a build preset.'
      return Promise.resolve(false)
    }
    return ctx.runReporting<BooleanMutation>(RaidPlayerApplyBuildPresetDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      raidPlayerId: Number(playerId),
      presetId: Number(presetId),
      source,
    }, (result) => result.raidPlayerApplyBuildPreset === true)
  }

  return {
    fetchMyBuildPresets,
    createMyBuildPreset,
    updateMyBuildPreset,
    removeMyBuildPreset,
    createGuildBuildPreset,
    updateGuildBuildPreset,
    removeGuildBuildPreset,
    applyBuildPreset,
  }
}
